/**
 * Patient Registration Page
 * Secure patient-only registration with email verification
 * Staff/Admin registration is handled via invitation system only
 * Supports promo codes and referral codes for discounts
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  AlertCircle, Loader2, Building2, CheckCircle2, Mail, Eye, EyeOff,
  Tag, Gift, Percent, X, Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import Logo from '../components/Logo';

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const { clinicSlug: clinicSlugParam } = useParams(); // From /join/:clinicSlug route
  const clinicSlug = clinicSlugParam || searchParams.get('clinic');
  const clinicIdFromUrl = searchParams.get('clinic_id');
  const refCodeFromUrl = searchParams.get('ref'); // Referral code from URL
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone: '',
    discountCode: refCodeFromUrl || '' // Pre-fill if ref code in URL
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clinicLoading, setClinicLoading] = useState(true);
  const [clinic, setClinic] = useState(null);
  const [registered, setRegistered] = useState(false);
  const [error, setError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [codeError, setCodeError] = useState('');
  const navigate = useNavigate();

  // Fetch clinic info if slug or ID provided
  useEffect(() => {
    const fetchClinic = async () => {
      if (!clinicSlug && !clinicIdFromUrl) {
        setClinicLoading(false);
        return;
      }

      try {
        setClinicLoading(true);
        let response;
        
        if (clinicSlug) {
          response = await axios.get(`${API}/public/clinic-by-slug/${clinicSlug}`);
        } else if (clinicIdFromUrl) {
          response = await axios.get(`${API}/public/clinic-by-id/${clinicIdFromUrl}`);
        }
        
        if (response?.data) {
          setClinic(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch clinic:', error);
        // Don't show error, just allow generic registration
      } finally {
        setClinicLoading(false);
      }
    };

    fetchClinic();
  }, [clinicSlug, clinicIdFromUrl]);

  // Auto-validate referral code from URL
  useEffect(() => {
    if (refCodeFromUrl && clinic?.id) {
      validateDiscountCode(refCodeFromUrl);
    }
  }, [refCodeFromUrl, clinic]);

  const validateDiscountCode = async (code) => {
    if (!code.trim() || !clinic?.id) return;
    
    setValidatingCode(true);
    setCodeError('');
    
    try {
      // Try referral code first
      try {
        const refResponse = await axios.post(
          `${API}/marketing/referrals/validate?code=${code.toUpperCase()}&clinic_id=${clinic.id}`
        );
        
        if (refResponse.data.valid) {
          setAppliedDiscount({
            type: 'referral',
            code: code.toUpperCase(),
            discount_percent: refResponse.data.discount_percent,
            referrer_name: refResponse.data.referrer_name
          });
          setValidatingCode(false);
          return;
        }
      } catch (refError) {
        // Not a valid referral code, try promo code
      }
      
      // Try promo code
      try {
        const promoResponse = await axios.post(
          `${API}/marketing/promos/validate?clinic_id=${clinic.id}`,
          { code: code.toUpperCase() }
        );
        
        if (promoResponse.data.valid) {
          setAppliedDiscount({
            type: 'promo',
            code: code.toUpperCase(),
            discount_type: promoResponse.data.discount_type,
            discount_value: promoResponse.data.discount_value,
            name: promoResponse.data.name
          });
          setValidatingCode(false);
          return;
        }
      } catch (promoError) {
        // Not a valid promo code either
      }
      
      setCodeError('Invalid code. Please check and try again.');
      setAppliedDiscount(null);
    } catch (error) {
      setCodeError('Failed to validate code');
      setAppliedDiscount(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleCodeChange = (e) => {
    const code = e.target.value;
    setFormData({ ...formData, discountCode: code });
    setCodeError('');
    
    // Clear applied discount if code is cleared
    if (!code.trim()) {
      setAppliedDiscount(null);
    }
  };

  const handleApplyCode = () => {
    if (formData.discountCode.trim()) {
      validateDiscountCode(formData.discountCode);
    }
  };

  const handleRemoveCode = () => {
    setFormData({ ...formData, discountCode: '' });
    setAppliedDiscount(null);
    setCodeError('');
  };

  const getDiscountDisplay = () => {
    if (!appliedDiscount) return null;
    
    if (appliedDiscount.type === 'referral') {
      return `${appliedDiscount.discount_percent}% OFF`;
    }
    
    if (appliedDiscount.discount_type === 'percent') {
      return `${appliedDiscount.discount_value}% OFF`;
    } else if (appliedDiscount.discount_type === 'fixed') {
      return `$${appliedDiscount.discount_value} OFF`;
    } else if (appliedDiscount.discount_type === 'free_month') {
      return `${appliedDiscount.discount_value} Month${appliedDiscount.discount_value > 1 ? 's' : ''} FREE`;
    }
    
    return 'Discount Applied';
  };

  const validateForm = () => {
    if (!formData.full_name.trim()) {
      setError('Please enter your full name');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      const registrationData = {
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
        full_name: formData.full_name.trim(),
        phone: formData.phone.trim() || null,
        role: 'patient', // Always patient - staff/admin via invitation only
        clinic_id: clinic?.id || clinicIdFromUrl || null
      };

      // Add discount code if applied
      if (appliedDiscount) {
        if (appliedDiscount.type === 'referral') {
          registrationData.referral_code = appliedDiscount.code;
        } else {
          registrationData.promo_code = appliedDiscount.code;
        }
      }

      const response = await axios.post(`${API}/auth/register-patient`, registrationData);
      
      setRegistered(true);
      
      // Show success message with discount info
      let successMessage = 'Registration successful! Please check your email to verify your account.';
      if (response.data.promo_applied) {
        successMessage += ` Your ${response.data.promo_applied.name} discount will be applied to your first membership.`;
      }
      if (response.data.referral_applied) {
        successMessage += ` Your ${response.data.referral_applied.percent}% referral discount will be applied to your first membership.`;
      }
      
      toast.success(successMessage);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Registration failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while fetching clinic
  if (clinicLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-teal-600 animate-spin mb-4" />
            <p className="text-gray-600">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show success message after registration
  if (registered) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <Logo className="h-16 w-16" />
              <div className="flex flex-col">
                <span className="text-3xl font-bold text-gray-900">MyMedMember</span>
                <span className="text-xs text-gray-500">by Clinicax Systems</span>
              </div>
            </div>
            <div className="flex flex-col items-center space-y-4 pt-4">
              <div className="p-4 bg-green-100 rounded-full">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <CardTitle className="text-xl text-center text-green-800">Check Your Email!</CardTitle>
              <CardDescription className="text-center">
                We have sent a verification link to <strong>{formData.email}</strong>. 
                Please click the link to activate your account.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Discount Applied Notice */}
            {appliedDiscount && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 text-green-800">
                  <Gift className="h-5 w-5" />
                  <span className="font-medium">Discount Applied!</span>
                </div>
                <p className="text-sm text-green-700 mt-1">
                  {appliedDiscount.type === 'referral' 
                    ? `${appliedDiscount.discount_percent}% off (referred by ${appliedDiscount.referrer_name})`
                    : `${appliedDiscount.name}: ${getDiscountDisplay()}`
                  } will be applied to your first membership.
                </p>
              </div>
            )}

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-800 mb-1">What is next?</h4>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>1. Check your email inbox (and spam folder)</li>
                    <li>2. Click the verification link</li>
                    <li>3. Sign in to access your account</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col gap-2">
              <Button 
                variant="outline"
                onClick={() => navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`)}
                className="w-full"
              >
                Did not receive the email?
              </Button>
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-teal-600 hover:bg-teal-700"
              >
                Go to Sign In
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 flex items-center justify-center p-4">
      {/* Back to Home Link */}
      <Link 
        to="/" 
        className="absolute top-4 left-4 text-teal-600 hover:text-teal-700 flex items-center space-x-2"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m12 19-7-7 7-7"/>
          <path d="M19 12H5"/>
        </svg>
        <span>Back to Home</span>
      </Link>
      
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-center space-x-3">
            {clinic?.logo_url ? (
              <img src={clinic.logo_url} alt={clinic.name} className="h-16 w-16 object-contain rounded-lg" />
            ) : (
              <Logo className="h-16 w-16" />
            )}
            <div className="flex flex-col">
              <span className="text-3xl font-bold text-gray-900">
                {clinic?.name || 'MyMedMember'}
              </span>
              {clinic && (
                <span className="text-xs text-gray-500">Membership Registration</span>
              )}
              {!clinic && (
                <span className="text-xs text-gray-500">by Clinicax Systems</span>
              )}
            </div>
          </div>
          
          <CardTitle className="text-2xl text-center" data-testid="register-title">
            Create Patient Account
          </CardTitle>
          <CardDescription className="text-center">
            {clinic 
              ? `Join ${clinic.name} as a member`
              : 'Register to access healthcare membership services'
            }
          </CardDescription>

          {/* Clinic Info Badge */}
          {clinic && (
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-full text-sm">
                <Building2 className="h-4 w-4" />
                <span>{clinic.name}</span>
              </div>
            </div>
          )}

          {/* Staff/Admin Note */}
          <Alert className="bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 text-xs">
              <strong>For Staff/Admin:</strong> Registration is by invitation only. 
              Please contact your clinic administrator for an invitation.
            </AlertDescription>
          </Alert>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleRegister} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                placeholder="John Doe"
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                required
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number (Optional)</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                data-testid="register-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  className="pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">Minimum 8 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  required
                  className="pr-10"
                  data-testid="register-confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  data-testid="toggle-confirm-password-visibility"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Promo/Referral Code Section */}
            {clinic && (
              <div className="space-y-2 pt-2 border-t">
                <Label htmlFor="discountCode" className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-purple-600" />
                  Promo or Referral Code
                </Label>
                
                {appliedDiscount ? (
                  // Show applied discount
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">
                          {appliedDiscount.code}
                        </p>
                        <p className="text-xs text-green-700">
                          {appliedDiscount.type === 'referral' 
                            ? `Referred by ${appliedDiscount.referrer_name}`
                            : appliedDiscount.name
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-600 text-white">
                        {getDiscountDisplay()}
                      </Badge>
                      <button
                        type="button"
                        onClick={handleRemoveCode}
                        className="p-1 hover:bg-green-100 rounded"
                      >
                        <X className="h-4 w-4 text-green-700" />
                      </button>
                    </div>
                  </div>
                ) : (
                  // Show code input
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        id="discountCode"
                        placeholder="Enter code (e.g., WELCOME25)"
                        value={formData.discountCode}
                        onChange={handleCodeChange}
                        className="uppercase"
                        data-testid="register-discount-code-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleApplyCode}
                        disabled={!formData.discountCode.trim() || validatingCode}
                        className="shrink-0"
                      >
                        {validatingCode ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Apply'
                        )}
                      </Button>
                    </div>
                    {codeError && (
                      <p className="text-xs text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {codeError}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      Have a promo code or referral code? Enter it above to get a discount.
                    </p>
                  </div>
                )}
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-teal-600 hover:bg-teal-700" 
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  Create Account
                  {appliedDiscount && (
                    <Badge variant="secondary" className="ml-2 bg-white/20">
                      {getDiscountDisplay()}
                    </Badge>
                  )}
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-6 space-y-2">
            <div className="text-center text-sm">
              <span className="text-gray-600">Already have an account? </span>
              <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium" data-testid="register-login-link">
                Sign in here
              </Link>
            </div>
            
            {/* Clinic Registration Link */}
            <div className="text-center text-sm pt-2 border-t">
              <span className="text-gray-600">Are you a clinic? </span>
              <Link to="/register-clinic" className="text-teal-600 hover:text-teal-700 font-medium">
                Register your clinic
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
